import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone, Share, Plus } from 'lucide-react';

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone || 
                       document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listen for beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after a delay if not dismissed before
      const dismissed = localStorage.getItem('pwa-prompt-dismissed');
      const lastDismissed = dismissed ? new Date(dismissed) : null;
      const daysSinceDismissed = lastDismissed ? 
        (new Date() - lastDismissed) / (1000 * 60 * 60 * 24) : null;

      if (!dismissed || daysSinceDismissed > 7) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Show iOS prompt if on iOS and not installed
    if (iOS && !standalone) {
      const dismissed = localStorage.getItem('pwa-prompt-dismissed');
      const lastDismissed = dismissed ? new Date(dismissed) : null;
      const daysSinceDismissed = lastDismissed ? 
        (new Date() - lastDismissed) / (1000 * 60 * 60 * 24) : null;

      if (!dismissed || daysSinceDismissed > 7) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response: ${outcome}`);
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', new Date().toISOString());
  };

  // Don't show if already installed
  if (isStandalone || !showPrompt) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up">
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl border border-cyan-500/30 shadow-2xl shadow-cyan-500/10 p-4">
        <button 
          onClick={handleDismiss}
          className="absolute top-3 right-3 text-gray-400 hover:text-white transition"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-7 h-7 text-white" />
          </div>
          
          <div className="flex-1">
            <h3 className="text-white font-bold text-lg mb-1">Install Baselinx Mobile</h3>
            <p className="text-gray-400 text-sm mb-3">
              Add to your home screen for the full app experience with offline access.
            </p>
            
            {isIOS ? (
              <div className="bg-slate-700/50 rounded-lg p-3 text-sm">
                <p className="text-gray-300 mb-2">To install on iOS:</p>
                <ol className="text-gray-400 space-y-1">
                  <li className="flex items-center gap-2">
                    <span className="bg-cyan-500/20 text-cyan-400 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                    Tap <Share className="w-4 h-4 inline text-cyan-400" /> Share button
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="bg-cyan-500/20 text-cyan-400 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                    Tap <Plus className="w-4 h-4 inline text-cyan-400" /> Add to Home Screen
                  </li>
                </ol>
              </div>
            ) : (
              <button
                onClick={handleInstall}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-4 py-2.5 rounded-lg font-bold flex items-center justify-center gap-2 transition"
              >
                <Download className="w-5 h-5" />
                Install App
              </button>
            )}
          </div>
        </div>
        
        <div className="mt-3 pt-3 border-t border-slate-700 flex items-center justify-center gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
            Works Offline
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-cyan-500 rounded-full"></span>
            Fast & Native
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
            Push Notifications
          </span>
        </div>
      </div>
      
      <style jsx>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PWAInstallPrompt;
